<template>
  <div class="unshipped-items-grid">
    <div v-if="loading" class="loader"></div>
    <div v-else class="form-cnt">
      <div v-if="rowData.length > 0" class="form grid-cnt">
        <ag-grid-vue
          class="ag-theme-balham"
          :grid-options="gridOptions"
          :column-defs="columnDefinitions"
          :default-col-def="defaultColDef"
          :row-data="rowData"
          :modules="modules"
          :pagination="enablePagination"
          :pagination-page-size="pageSize"
          :get-row-height="getRowHeight"
          :suppress-menu-hide="true"
          :row-class-rules="rowClassRules"
          v-on:first-data-rendered="onFirstDataRendered"
          v-on:grid-ready="onGridReady">
        </ag-grid-vue>
      </div>
      <div v-else class="no-rows">
        {{ $t('viewOrders.no_items_message') }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { AgGridVue } from '@ag-grid-community/vue3';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { GridApi } from '@ag-grid-community/core';
import { OrderItemsService } from '@/services/order-items.service';
import { LegalEntityEnum, OrderItemDto, OrderType } from '@onrunning/backstage-models';
import { utilsMixins } from '@/mixins/utils.mixins';
import { ordersMixins } from '@/mixins/orders.mixins';
import { GridConfig } from '@/models/grid-config';
import { GridNames } from '@/models/grids-names.enum';
import { useUserStore } from '../../stores/user';
import { storeToRefs } from 'pinia';
import { useGridsStore } from '../../stores/grids';
import { useGrid } from '../../composables/grid';
import { useLocale } from '../../composables/locale';

export default defineComponent({
  name: 'unshipped-items-grid',
  components: {
    AgGridVue,
  },
  props: {
    enablePagination: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const { userLocale } = useLocale();
    const { addEditGridConfig, getGridByName } = useGridsStore();
    const { defaultGridOptions, dateColumnDefinition, applyGridConfig } = useGrid();

    return { userLocale, addEditGridConfig, getGridByName, defaultGridOptions, dateColumnDefinition, applyGridConfig };
  },
  data() {
    const { selectedAccountId, selectedAccount } = storeToRefs(useUserStore());

    return {
      accountId: selectedAccountId,
      account: selectedAccount,
      items: new Array<OrderItemDto>(),
      loading: true,
      itemsService: new OrderItemsService(),
      columnDefinitions: new Array<any>(),
      rowClassRules: undefined as any,
      defaultColDef: {
        resizable: true,
        filter: true,
        sortable: true,
        minWidth: 80,
      },
      gridOptions: {
        ...this.defaultGridOptions(),
        suppressPaginationPanel: !this.enablePagination,
      },
      gridApi: undefined as GridApi | undefined,
      rowPixelsOffset: 1,
      pageSize: 50,
      modules: [ClientSideRowModelModule],
      initialGridConfig: new GridConfig() as Partial<GridConfig> | undefined,
      gridName: GridNames.BackorderItems,
    };
  },
  computed: {
    rowData(): Array<any> {
      const rowData = this.items.map((unshippedItem) => ({
        ...unshippedItem,
        requestedDate: unshippedItem.requestedDate
          ? new Date(unshippedItem.requestedDate)
          : unshippedItem.requestedDate,
        refillDate: unshippedItem.refillDate ? new Date(unshippedItem.refillDate) : unshippedItem.refillDate,
      }));

      return rowData;
    },
  },
  watch: {
    accountId(newVal) {
      if (newVal) {
        this.fetchItems();
      }
    },
    userLocale(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.gridApi?.refreshHeader();
          this.gridApi?.redrawRows();
        });
      }
    },
  },
  mounted() {
    this.$emit('backorder-items-loaded', false);

    this.initialGridConfig = this.getGridByName(this.gridName);
    this.gridOptions.onPaginationChanged = this.onPaginationChanged;
    this.gridOptions.onFilterChanged = this.gridFilterEventHandler;
    this.gridOptions.onSortChanged = this.onSortChanged;

    this.fetchItems();
  },
  beforeUnmount() {
    this.gridApi = undefined;
  },
  methods: {
    ...utilsMixins,
    ...ordersMixins,
    async fetchItems() {
      this.$emit('backorder-items-loaded', false);
      this.loading = true;

      const validation = await this.itemsService.getUnshippedItems(this.accountId as string);

      if (validation.model) {
        this.items = validation.model;
      } else {
        this.$snotify.error(this.$t('global.error'));
      }

      this.initializeTable();

      window.addEventListener('resize', this.adjustColumns);

      this.loading = false;
      this.$emit('backorder-items-loaded', true);
    },
    initializeTable() {
      if (this.columnDefinitions.length === 0) {
        this.columnDefinitions = [
          {
            headerValueGetter: () => this.$t('viewOrders.items_list_columns.item_number'),
            field: 'itemNumber',
            suppressSizeToFit: true, // width: 80
          },
          {
            headerValueGetter: () => this.$t('viewOrders.items_list_columns.description'),
            field: 'description',
            suppressSizeToFit: true, //  width: 120
          },
          {
            headerValueGetter: () => this.$t('viewOrders.items_list_columns.totalToBeDelivered'),
            field: 'totalToBeDelivered',
            suppressSizeToFit: true,
          },
          {
            headerValueGetter: () => this.$t('viewOrders.items_list_columns.order_number'),
            field: 'orderNumber',
            suppressSizeToFit: true,
            cellClass: ['clickable-cell'],
            cellRenderer: this.renderOpenOrderLink,
          },
          {
            headerValueGetter: () => this.$t('viewOrders.items_list_columns.customer_po_number'),
            field: 'customerPoNumber',
            suppressSizeToFit: true,
          },
          {
            headerValueGetter: () => this.$t('viewOrders.items_list_columns.order_type'),
            field: 'orderType',
            suppressSizeToFit: true,
            valueFormatter: this.displayOrderTypeWithFieldName,
          },
          this.dateColumnDefinition({
            headerValueGetter: () => this.$t('viewOrders.items_list_columns.requested_date'),
            field: 'requestedDate',
            sort: 'asc',
          }),
          {
            headerValueGetter: () => this.$t('viewOrders.items_list_columns.item_status'),
            field: 'status',
            valueGetter: (params: any) => this.$t(`viewOrders.item_status['${params.data.status}']`),
          },
          // ToDo: Uncomment when refillDate is available
          // this.dateColumnDefinition({
          //  headerValueGetter: () => this.$t('viewOrders.items_list_columns.refillDate'),
          //  field: 'refillDate',
          // }),
        ];
      }
    },
    getRowHeight() {
      // ToDo is it possible to improve 0?
      return this.gridOptions.rowHeight ?? 0 - this.rowPixelsOffset;
    },
    onGridReady(params: any) {
      this.gridApi = params.api;

      if (this.initialGridConfig) {
        this.applyGridConfig(this.initialGridConfig, this.gridApi as GridApi);
      }
    },
    onFirstDataRendered() {
      this.adjustColumns();
    },
    adjustColumns() {
      this.gridApi?.autoSizeAllColumns();
      this.gridApi?.sizeColumnsToFit();
    },
    removeAllGridFilters() {
      this.gridApi?.setFilterModel(null);
    },
    renderOpenOrderLink(params: any) {
      let path = '';
      const withRelatedOrders = params.data.type === OrderType.MultiplePreOrder;
      const query = withRelatedOrders ? { showRelatedOrders: 'true' } : {};

      path = `/orders/${params.data.orderNumber}`;

      const link = document.createElement('a');

      link.href = this.$router.resolve({ path, query }).href;
      link.innerText = params.value;

      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.$router.push(path);
      });

      return link;
    },
    onPaginationChanged(gridOptions: any) {
      if (gridOptions.api) {
        const totalRows = gridOptions.api.paginationGetRowCount() || 0;

        this.$emit('update-row-count', totalRows);
        this.addEditGridConfig({
          name: this.gridName,
          pageNumber: gridOptions.api.paginationGetCurrentPage(),
        });
      }
    },
    displayOrderTypeWithFieldName(params: any) {
      return this.displayOrderType(params, 'orderType', this.account?.legalEntity as LegalEntityEnum, this.$t);
    },
    gridFilterEventHandler(params: { api?: GridApi }) {
      const currentFilterModel = params.api?.getFilterModel();

      if (currentFilterModel) {
        this.addEditGridConfig({ name: this.gridName, filters: currentFilterModel });
      }
    },
    onSortChanged() {
      const sortedColumn = this.gridApi?.getColumnState();

      if (sortedColumn) {
        this.addEditGridConfig({ name: this.gridName, sorting: sortedColumn });
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.unshipped-items-grid {
  .ag-theme-balham {
    width: 100%;
    height: 100%;
  }

  .form-cnt {
    .grid-cnt {
      padding: 0;
      border-top: 1px var(--gray-color)-border solid;
      border-bottom: 1px var(--gray-color)-border solid;
    }
  }
}
</style>
